import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "@/contexts/auth.ctx.tsx";
import { useLocalStorage } from "@mantine/hooks";
import { type OEJTresult } from "@/q-n-a.ts";
import { CacheManager } from "@/utils/cache.man.tsx";
import type { TestEntry } from "@prisma/client";
import { DateTime } from "luxon";

type ResultResponse = { results: TestEntry[] }

interface ResultsContextType {
    resultList: ResultResponse['results'];
    selectedResult: TestEntry | null;
    /** Gets the list of results, but also used to refresh */
    getResultList: (forceRefresh?: boolean) => Promise<ResultResponse['results']>;
    getFilteredResultList: (profileId?: string, sortAsc?: boolean) => TestEntry[];
    getResultScores: (id: string) => Promise<OEJTresult>;
    getResultIntakeAnswers: (id: string) => Promise<{ info: Record<string, string | number | boolean> }>;
    getMarkdownTxt: (id: string) => Promise<string>;
    selectResult: (result: TestEntry | string | null) => void;
}

const ResultsContext = createContext<ResultsContextType | undefined>(undefined);

export const ResultsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { injectHeader, accessor, isAuthenticated } = useAuth();
    const cacheMan = CacheManager.Instance();
    const [selectedResult, setSelectedResult] = useState<TestEntry | null>(null);
    const [results, setResults] = useLocalStorage<ResultResponse['results']>({
        key: 'resultList',
        defaultValue: []
    });

    useEffect(() => {
        if (accessor?.id) {
            getResultList();
        }
    }, [accessor?.id]);

    useEffect(() => {
        if(!isAuthenticated) {
            setResults([]);
        }
    }, [isAuthenticated]);

    const getResultList = async (forceRefresh = false) => {
        if (!forceRefresh && results.length > 0) {
            console.log('Returning cached results from local storage');
            return results;
        }
        const req = new Request(`/api/auth/results/list`);
        injectHeader(req);
        const res = await fetch(req); // Do not cache here
        const data = await res.json() as ResultResponse;
        setResults(data.results);
        return data.results;
    }

    const selectResult = (result: TestEntry | string | null) => {
        if (typeof result === 'string') {
            setSelectedResult(results.find(r => r.id === result) || null);
        } else {
            setSelectedResult(result);
        }
    }

    /**
     * Function to get the markdown from OUR specific api
     * @param id - The id of the markdown file to get
     * @returns The markdown text
     */
    async function getMarkdownTxt(id: string) {
        const response = await cacheMan.fetch(new Request(`/api/library/${id}`));
        if (!response.ok) {
            throw new Error('Failed to fetch markdown');
        }
        return await response.text();
    }

    /**
     * Get the answers to the questions asked at the end of the testing process
     * @param id - the id of the test
     */
    async function getResultIntakeAnswers(id: string) {
        const req = new Request(`/api/results/${id}/info`);
        injectHeader(req);
        const res = await cacheMan.fetch(req);
        return await res.json() as { info: Record<string, string | number | boolean> };
    }

    /**
     * Get the scores for the test, the 5 scores for each of the 5 factors as numbers between -1 and 1
     * @param id - the id of the test
     * @returns The scores for the test
     */
    async function getResultScores(id: string) {
        const req = new Request(`/api/results/${id}`);
        injectHeader(req);
        const res = await cacheMan.fetch(req);
        return (await res.json() as { breakdown: OEJTresult }).breakdown;
    }

    function getFilteredResultList(profileId?: string, sortAsc = true) {
        const sortedResults = results.sort((a, b) => {
            // @ts-ignore
            return sortAsc ? DateTime.fromISO(b.createdAt as string).toMillis() - DateTime.fromISO(a.createdAt as string).toMillis() : DateTime.fromISO(a.createdAt as string).toMillis() - DateTime.fromISO(b.createdAt as string).toMillis();
        });
        if(!profileId) return sortedResults;
        return sortedResults.filter(result => result.testAccessId === profileId);
    }

    return (<ResultsContext.Provider value={{
        resultList: results,
        selectedResult,
        selectResult,
        getResultList,
        getResultScores,
        getResultIntakeAnswers,
        getMarkdownTxt,
        getFilteredResultList
    }}>{children}</ResultsContext.Provider>)
}

export const useResults = () => {
    const context = useContext(ResultsContext);
    if (!context) {
        throw new Error('useResults must be used within a ResultsProvider');
    }
    return context;
}

export type { ResultsContextType };
export { ResultsContext };